import { ArrowUpward, CheckCircleRounded, ErrorRounded, PictureAsPdf } from '@mui/icons-material';
import { Box, List, ListItem, ListItemButton, ListItemText, Typography } from '@mui/material';
import { setInspectionSelectedItem, setInspectionSortSelectedItem } from 'app/actions/checklist';
import { Autosizer, Scrollbar } from 'app/components';
import React, { createContext, forwardRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FixedSizeList } from 'react-window';

const ChecklistInspectionListContext = createContext();

const getInspectionTableColumns = () => (config.clientUi === 'voykan' ? config.inspectionColumnsVoykan : config.inspectionColumns);

const ChecklistInspectionIcons = {
  1: <CheckCircleRounded fontSize='small' color='success' />,
  0: <ErrorRounded fontSize='small' color='error' />,
};

const ChecklistInspectionIcon = ({ status }) => {
  const Component = ChecklistInspectionIcons[status];
  return Component ? Component : null;
};

const ChecklistInspectionList = () => {
  const dispatch = useDispatch();
  const { inspectionItems, inspectionSelectedItem, inspectionSearchValue, inspectionSortSelectedItem, inspectionSelectedFilters } =
    useSelector((state) => state.checklist);

  const sortItems = useMemo(() => {
    const compareFn = (a, b) => {
      switch (inspectionSortSelectedItem.name) {
        case 'created_at':
          switch (inspectionSortSelectedItem.order) {
            case 'ASC':
              return new Date(a[inspectionSortSelectedItem.name]) - new Date(b[inspectionSortSelectedItem.name]);
            case 'DESC':
              return new Date(b[inspectionSortSelectedItem.name]) - new Date(a[inspectionSortSelectedItem.name]);
          }
          break;
        case 'number':
        case 'trailer':
        case 'group_name':
        case 'transport_group_name':
        case 'autocolon_name':
        case 'employee': {
          const key = inspectionSortSelectedItem.name;
          const av = String(a[key] ?? '');
          const bv = String(b[key] ?? '');
          switch (inspectionSortSelectedItem.order) {
            case 'ASC':
              return av.localeCompare(bv, {
                sensitivity: 'base',
              });
            case 'DESC':
              return bv.localeCompare(av, {
                sensitivity: 'base',
              });
          }
          break;
        }
        case 'status':
          switch (inspectionSortSelectedItem.order) {
            case 'ASC':
              return a.status - b.status;
            case 'DESC':
              return b.status - a.status;
          }
          break;
      }
    };

    const filterByFilters = (item) => {
      const checkFilter = (filterName) => {
        const filter = inspectionSelectedFilters[filterName];
        switch (filterName) {
          case 'status':
          case 'template_id':
          case 'employee_id':
          case 'transport_id':
            return filter.length === 0 || filter.includes(item[filterName]);
          case 'transport_group_id':
            return filter.length === 0 || (item.transport_group_id != null && filter.includes(Number(item.transport_group_id)));
          case 'autocolon_id':
            if (filter.length === 0) return true;
            return (item.autocolon_ids || []).some((id) => filter.includes(Number(id)));
          case 'from':
            return !filter || moment(item.created_at).isSameOrAfter(filter);
          case 'to':
            return !filter || moment(item.created_at).isSameOrBefore(filter);
          default:
            return true;
        }
      };

      return Object.keys(inspectionSelectedFilters).every((filterName) => checkFilter(filterName));
    };

    const q = inspectionSearchValue.toLowerCase();
    const filterBySearch = inspectionSearchValue.length
      ? inspectionItems.filter((item) => {
          const inField = (v) => v != null && String(v).toLowerCase().indexOf(q) !== -1;
          return (
            inField(item.number) ||
            inField(item.employee) ||
            (config.clientUi === 'voykan' && (inField(item.transport_group_name) || inField(item.autocolon_name))) ||
            (config.clientUi !== 'voykan' && inField(item.group_name))
          );
        })
      : inspectionItems;

    return filterBySearch.filter(filterByFilters).sort(compareFn);
  }, [inspectionItems, inspectionSearchValue, inspectionSortSelectedItem, inspectionSelectedFilters]);

  const handleItemClick = (value) => () => dispatch(setInspectionSelectedItem(value));

  const handleSortClick = (value) => () => dispatch(setInspectionSortSelectedItem(value));

  return (
    <Box
      component='div'
      sx={(theme) => ({
        width: '100%',
        height: '100%',
        '.simplebar-offset > div': {
          '&::-webkit-scrollbar': {
            width: 0,
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
            boxShadow: 'none',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        },
        '.simplebar-horizontal': {
          right: '10px',
        },
        '.simplebar-vertical': {
          top: '30px',
          bottom: '0',
        },
        '.MuiListItem-root': {
          width: '100%',
          userSelect: 'none',
          padding: theme.spacing(0),
          borderRadius: '0',
        },
      })}>
      <Autosizer>
        {({ height, width }) => (
          <Box sx={{ height, width }}>
            <ChecklistInspectionListContext.Provider
              value={{
                sortItemClick: handleSortClick,
                sortSelectedItem: inspectionSortSelectedItem,
              }}>
              <Scrollbar>
                {({ scrollableNodeRef, contentNodeRef }) => (
                  <FixedSizeList
                    height={height}
                    itemSize={24}
                    overscanCount={10}
                    itemCount={sortItems.length}
                    innerRef={contentNodeRef}
                    outerRef={scrollableNodeRef}
                    innerElementType={InspectionListInnerElementType}
                    itemData={{
                      items: sortItems,
                      selectedItem: inspectionSelectedItem,
                      onClick: handleItemClick,
                    }}>
                    {InspectionListItemRender}
                  </FixedSizeList>
                )}
              </Scrollbar>
            </ChecklistInspectionListContext.Provider>
          </Box>
        )}
      </Autosizer>
    </Box>
  );
};

const InspectionListInnerElementType = forwardRef(({ children, style, ...rest }, ref) => (
  <ChecklistInspectionListContext.Consumer>
    {({ sortItemClick, sortSelectedItem }) => (
      <List
        ref={ref}
        disablePadding
        sx={{
          height: style.height,
          minHeight: '100%',
        }}
        {...rest}>
        <ListItem
          sx={(theme) => ({
            position: 'absolute',
            top: '0',
            left: '0',
            width: '100%',
            height: style.height,
            minHeight: '100%',
            zIndex: '2',
            pointerEvents: 'none',
            padding: theme.spacing(0),
            '.MuiListItemText-root': {
              display: 'flex',
              justifyContent: 'space-between',
              height: '100%',
              position: 'relative',
              flex: 'none',
              '&:before': {
                content: '""',
                position: 'absolute',
                top: '0',
                right: '0',
                height: '100%',
                bgcolor: theme.palette.divider,
                width: '1px',
                zIndex: '1',
                pointerEvents: 'none',
              },
              '&:last-of-type': {
                flex: '1',
                '&:before': {
                  display: 'none',
                },
              },
            },
          })}>
          {getInspectionTableColumns().map((column) => (
            <ListItemText key={column.name} style={{ width: column.width }} />
          ))}
        </ListItem>
        <ListItem
          disablePadding
          sx={(theme) => ({
            width: '100%',
            display: 'flex',
            position: 'sticky !important',
            top: '0',
            zIndex: '1',
            '.MuiListItemButton-root': {
              display: 'flex',
              flex: 'none',
              padding: theme.spacing(0),
              position: 'relative',
              bgcolor: 'background.default',
              borderColor: theme.palette.divider,
              '&:first-of-type': {
                '.MuiListItemText-root': {
                  paddingLeft: theme.spacing(2),
                },
              },
              '&:last-of-type': {
                flex: '1',
                '.MuiListItemText-root': {
                  paddingRight: theme.spacing(2),
                },
              },
              '.MuiTouchRipple-rippleVisible': {
                color: 'inherit',
              },
              '.MuiListItemText-root': {
                display: 'flex',
                cursor: 'pointer',
                margin: theme.spacing(0),
                padding: '6px 6px',
                alignItems: 'center',
                justifyContent: 'space-between',
                '.MuiTypography-root': {
                  display: 'flex',
                  '&:nth-of-type(1)': {
                    color: 'text.secondary',
                    marginRight: '4px',
                  },
                  '&:nth-of-type(2) svg': {
                    fontSize: '12px',
                    transition: 'all 300ms',
                    transform: 'rotateZ(0deg)',
                  },
                },
              },
            },
          })}>
          {getInspectionTableColumns().map((column) => (
            <ListItemButton
              key={column.name}
              onClick={sortItemClick(column)}
              disableTouchRipple={!column.order}
              sx={(theme) => ({
                minWidth: column.width,
                '.MuiListItemText-root': {
                  display: 'flex',
                  justifyContent: 'space-between',
                  '.MuiTypography-root': {
                    width: 'auto',
                    '&:nth-of-type(2)': {
                      opacity: '0.2',
                      transform: 'rotateZ(0)',
                      transition: 'transform ' + theme.transitions.duration.shortest + 'ms ' + theme.transitions.easing.easeInOut + ' 0ms',
                      ...(sortSelectedItem.name === column.name && {
                        opacity: '1',
                        ...(sortSelectedItem.order === 'DESC' && {
                          transform: 'rotateZ(180deg)',
                        }),
                      }),
                    },
                  },
                },
              })}>
              <ListItemText disableTypography>
                {column.name === 'download' ? (
                  <Typography
                    component='span'
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      width: '100%',
                      color: 'text.secondary',
                    }}>
                    <PictureAsPdf fontSize='small' titleAccess={column.label} />
                  </Typography>
                ) : (
                  <>
                    <Typography>{column.label}</Typography>
                    <Typography>{column.order && <ArrowUpward fontSize='small' />}</Typography>
                  </>
                )}
              </ListItemText>
            </ListItemButton>
          ))}
        </ListItem>
        {children}
      </List>
    )}
  </ChecklistInspectionListContext.Consumer>
));

const InspectionListItemRender = ({ data, index, style }) => {
  const { items, selectedItem, onClick } = data;

  return (
    <ListItem
      key={index}
      disablePadding
      style={style}
      selected={items[index].id === selectedItem?.id}
      onClick={onClick(items[index])}
      sx={(theme) => ({
        width: '100%',
        marginTop: '30px',
        userSelect: 'none',
        borderRadius: '0',
        '&:hover': {
          '.MuiListItemText-root': {
            cursor: 'pointer',
          },
        },
        '&.Mui-selected': {
          '.MuiListItemText-root': {
            bgcolor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
          },
          '.MuiCheckbox-root': {
            color: theme.palette.primary.contrastText,
            '&.Mui-checked': {
              color: theme.palette.primary.contrastText,
            },
          },
        },
        '.MuiListItemText-root': {
          display: 'flex',
          flex: 'none',
          height: '100%',
          margin: theme.spacing(0),
          padding: theme.spacing(0.5, 1),
          alignItems: 'center',
          '&:first-of-type': {
            paddingLeft: theme.spacing(2),
            svg: {
              fontSize: '16px',
              marginRight: '3px',
            },
          },
          '&:last-of-type': {
            flex: '1',
            paddingRight: theme.spacing(2),
          },
          '.MuiTypography-root': {
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          },
          '.MuiCheckbox-root': {
            padding: theme.spacing(0),
            marginLeft: '-2px',
            svg: {
              fontSize: '18px',
            },
          },
        },
        ...((items.length - (items.length - 1 - index)) % 2 === 0 && {
          '& .MuiListItemText-root': {
            bgcolor: 'background.default',
          },
        }),
      })}>
      {getInspectionTableColumns().map((column) => (
        <ListItemText
          key={column.name}
          sx={{
            minWidth: column.width,
            ...(column.name === 'status' && {
              justifyContent: 'center',
              '.MuiTypography-root': {
                display: 'flex',
              },
            }),
          }}>
          {column.name === 'status' ? (
            <ChecklistInspectionIcon status={items[index].status} />
          ) : (
            <Typography>{column.format ? column.format(items[index][column.name]) : items[index][column.name]}</Typography>
          )}
        </ListItemText>
      ))}
    </ListItem>
  );
};

export default ChecklistInspectionList;
